import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { Icon, type IconName } from './Icon';
import styles from './ui.module.css';

export function Skeleton({ lines = 3, height = 56 }: { lines?: number; height?: number }) {
  const { t } = useTranslation();
  return (
    <div
      className={styles.skeletonGroup}
      aria-busy="true"
      aria-label={t('common.loading')}
      role="status"
    >
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} className={styles.skeleton} style={{ blockSize: height }} />
      ))}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  const { t } = useTranslation();
  return (
    <div className={styles.centered} role="status">
      <span className={styles.spinnerLarge} aria-hidden="true" />
      <span className="visually-hidden">{label ?? t('common.loading')}</span>
    </div>
  );
}

export function EmptyState({
  icon = 'cart',
  title,
  body,
  action,
}: {
  icon?: IconName;
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className={styles.empty}>
      <span className={styles.emptyIcon}>
        <Icon name={icon} size={28} />
      </span>
      <h2 className={styles.emptyTitle}>{title}</h2>
      {body && <p className={styles.emptyBody}>{body}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className={styles.empty} role="alert">
      <span className={[styles.emptyIcon, styles.emptyIconError].join(' ')}>
        <Icon name="close" size={28} />
      </span>
      <h2 className={styles.emptyTitle}>{t('errors.loadFailed')}</h2>
      <p className={styles.emptyBody}>{message}</p>
      {onRetry && (
        <Button icon="refresh" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      )}
    </div>
  );
}

export function Banner({
  tone = 'info',
  children,
  action,
}: {
  tone?: 'info' | 'warning';
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={[styles.banner, styles[`banner-${tone}`]].join(' ')} role="status">
      <span>{children}</span>
      {action}
    </div>
  );
}
